// Notes — Liz's own writing. Short posts about work she has shipped and
// decisions she is working through.
//
// Data sources (in priority order):
//   1. Supabase `notes` table — edited from the dashboard, no commit needed.
//   2. The static array below — seed and fallback, used when Supabase is
//      not configured, the query errors, or the table is empty.
//
// Post text is verbatim. Do not tighten, re-punctuate, or smooth the voice.
//
// To add a post by hand:
//   1. Drop any image in src/assets/img/ and import it below.
//   2. Add an entry with a unique id and a higher `order` than the current top.

import lensImg from "../assets/img/observatory-decomposition.jpg";
import iakmImg from "../assets/img/iakm.jpg";
import tapestryUpdateImg from "../assets/img/tapestry-update.jpg";
import knowledgeObservatoryImg from "../assets/img/knowledge-observatory.jpg";
import tapestryMarkImg from "../assets/img/tapestry-mark.jpg";
import choicesImg from "../assets/img/choices.jpg";
import { supabase } from "../lib/supabase";

export const notes = [
  {
    id: "the-lens",
    title: "The Lens",
    date: "2026-09-26",
    image: lensImg,
    link: "https://github.com/Lizo-RoadTown/lens-core",
    linkLabel: "Watch the build in lens-core",
    order: 6,
    body: `The Knowledge Observatory is inspiring new instruments for new systems, so I am creating the instrument as modular units that can be customized to match individual components and the systems they work in.

Tapestry is watching it get built. The main build is happening in lens-core, with Tapestry running its own observation process and telemetry on the decomposition and the build, so the instrument is producing a record of how it got made.

This is really cool stuff.`,
  },
  {
    id: "interface-anchored-knowledge-mapping",
    title: "Interface-Anchored Knowledge Mapping",
    date: "2026-09-09",
    image: iakmImg,
    link: "https://doi.org/10.5281/zenodo.22677663",
    linkLabel: "Read the papers on Zenodo",
    order: 5,
    body: `The methodology behind what most know as the Knowledge Observatory is out. It has a name now: Interface-Anchored Knowledge Mapping, and anyone can use it.

There are two papers, one on the methodology and one on the implementation. Both may go out to be published if I can manage it. For now they are just there, available for anyone to meander by and take a look. Version 2 is uploaded.

I wish I weren't so compelled to let my imagination run wild, but this is the result of wild imagination that loves to enforce rigid rules. I don't know why I am this way.`,
  },
  {
    id: "out-of-my-head",
    title: "Out of my head",
    date: "2026-09-05",
    image: tapestryUpdateImg,
    link: "https://tapestry-khaki.vercel.app/",
    linkLabel: "The Tapestry documentation",
    order: 4,
    body: `I have been sitting with where Tapestry is going, and most of it comes down to translation. Getting what I build out of my own head and into everyday language and everyday use.

Memory is the load-bearing part. Everything else sits on top of it, so it got the work first. The MCP is updated, a lot of the documentation is rewritten, and there is a glossary now. More is in progress, but the structure underneath is holding.

Tapestry is built to survive AI evolution. The AI tools and models end up as artifacts in an archive, so they are not the part worth keeping. What I need out of them is the principles that made them necessary for the system to work. So I am rewriting the observer, and writing those principles down as they settle. That is the slow part. The tools still matter, and I have a different use for them.

The tutorial is the part I cannot shortcut, and it is the same translation problem in its hardest form. Setups differ too much for one version of it, and I am the OG test user, so explaining this to someone else will show me everywhere it needs to generalize.

Then the next part, which is bigger than anything so far: organizational memory and multi-organizational telemetry.

Coming soon...`,
  },
  {
    id: "knowledge-observatory-open",
    title: "Knowledge Observatory is Open",
    date: "2026-08-17",
    image: knowledgeObservatoryImg,
    link: "https://github.com/Lizo-RoadTown/PROVES_LIBRARY",
    linkLabel: "See it on GitHub",
    order: 3,
    body: `I've been sitting on this one for a while, wondering whether it was worth doing anything with or whether it was silly. I don't know why I've been so self-conscious about it.

The Knowledge Observatory is now public under Apache 2.0. Anyone can use it, build on it, or cite it. The full methodology is still to come — I'm writing it up.

I built it hoping to create a method to map and make knowledge visible so it could be captured more easily and reduce how much we lose when we're creating new things or trying to teach people, but I haven't had the courage to start. Opening it up feels like the right first step. If other people can see the usefulness in it, that's worth more than sitting on it another year.

I'm hoping to attend some conferences and meet people who might have interest and be able to benefit. If you have questions, message me. I'd like to hear from you.`,
  },
  {
    id: "tapestry-published",
    title: "Tapestry",
    date: "2026-06-25",
    image: tapestryMarkImg,
    // The artwork's wordmark misspells PyPI as "PyPL". Crop it out rather
    // than editing the file; the logo mark alone sits in the upper third.
    imageCrop: "center 30%",
    link: "https://pypi.org/project/tapestry-cli/",
    linkLabel: "tapestry-cli on PyPI",
    order: 2,
    body: `I published my first Claude Code plugins and my first PyPI package. I'm not sure how to feel about it yet.

Every time I make a major change to the features, all of them have to change fundamentally too. While I'm sure seasoned software developers are skilled at this, I'm still working out what that workflow should look like.

Publishing anyway. If you've already solved this in your own work, I'd like to hear what is a replicable process for this.`,
  },
  {
    id: "grad-school-or-industry",
    title: "Grad school or industry",
    // No date: this is an open question, not an event.
    image: choicesImg,
    order: 1,
    body: `I haven't decided yet.

There are places in both where I think I could make a valuable contribution to the causes I'm most passionate about. Neither has shown me it outweighs the other. They diverge in domain, not in value.

What I care about is that the people doing work that will do good for humanity are supported. Wherever my contribution can be most effective is where I'll go. I'm still working out where that is.`,
  },
];

// Descending by explicit `order` when both have one, then descending by date.
// Posts with an `order` always precede those without.
export const sortNotes = (list) =>
  [...list].sort((a, b) => {
    const aHas = a.order != null;
    const bHas = b.order != null;
    if (aHas && bHas) return b.order - a.order;
    if (aHas) return -1;
    if (bHas) return 1;
    return (b.date || "").localeCompare(a.date || "");
  });

// Build from parts. `new Date("2026-08-17")` parses as UTC midnight and
// renders as the previous day in any negative-offset timezone.
export const formatPostDate = (iso) => {
  if (!iso) return null;
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

// ---- Supabase fetch ----
// Row shape (snake_case) is normalized to the camelCase the component uses.

const fromRow = (row) => ({
  id: row.id,
  title: row.title,
  date: row.date || undefined,
  body: row.body,
  image: row.image_url || undefined,
  imageCrop: row.image_crop || undefined,
  link: row.link || undefined,
  linkLabel: row.link_label || undefined,
  order: row.order != null ? row.order : undefined,
});

export const fetchNotes = async () => {
  if (!supabase) return notes;
  try {
    const { data, error } = await supabase.from("notes").select("*");
    if (error) {
      console.warn("Supabase notes fetch failed; using seed.", error);
      return notes;
    }
    if (!data || data.length === 0) return notes;
    return data.map(fromRow);
  } catch (err) {
    console.warn("Supabase notes fetch threw; using seed.", err);
    return notes;
  }
};
